/**
 * Drop investigation timeline (audit + SIEM merged).
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';

export type DropTimelineItemKind =
  | 'created'
  | 'updated'
  | 'sent'
  | 'killed'
  | 'failed'
  | 'other';

export type DropTimelineItem = {
  source: 'audit' | 'siem';
  id: string;
  occurredAt: string;
  summary: string;
  userEmail: string | null;
  action: string | null;
  success: boolean | null;
  kind: DropTimelineItemKind;
};

/** Controller body (before global TransformInterceptor wrap). */
export type DropTimelineResponse = {
  data: {
    uniqueId: string;
    from: string;
    to: string;
    items: DropTimelineItem[];
  };
};

/** Axios body after TransformInterceptor: { success, data: controllerBody, meta }. */
type TimelineApiEnvelope = {
  success: boolean;
  data: DropTimelineResponse;
  meta?: { timestamp: string; requestId: string };
};

export type DropTimelineScope = 'full' | 'story';

export const dropTimelineKeys = {
  all: ['drop-timeline'] as const,
  detail: (p: {
    uniqueId: string;
    wiserDropId?: number;
    preparedDropId?: string;
    scope: DropTimelineScope;
    siemLimit: number;
  }) => [...dropTimelineKeys.all, p] as const,
};

export type UseDropTimelineParams = {
  uniqueId?: string;
  wiserDropId?: number;
  preparedDropId?: string;
  scope?: DropTimelineScope;
  siemLimit?: number;
  enabled?: boolean;
};

const DEFAULT_STORY_SIEM = 50;
const DEFAULT_FULL_SIEM = 120;

export function useDropTimeline(params: UseDropTimelineParams) {
  const uid = (params.uniqueId ?? '').trim();
  const prep = (params.preparedDropId ?? '').trim();
  const scope = params.scope ?? 'full';
  const siemLimit =
    params.siemLimit ??
    (scope === 'story' ? DEFAULT_STORY_SIEM : DEFAULT_FULL_SIEM);
  const hasKey =
    uid.length > 0 ||
    prep.length > 0 ||
    (params.wiserDropId != null &&
      Number.isInteger(params.wiserDropId) &&
      params.wiserDropId >= 1);

  const cacheIdentity =
    uid ||
    (prep !== '' ? `__prepared:${prep}` : '') ||
    (params.wiserDropId != null && params.wiserDropId >= 1
      ? `__wiser:${params.wiserDropId}`
      : '__none');

  return useQuery({
    queryKey: dropTimelineKeys.detail({
      uniqueId: cacheIdentity,
      wiserDropId: params.wiserDropId,
      preparedDropId: prep || undefined,
      scope,
      siemLimit,
    }),
    queryFn: async () => {
      const res = await api.get<TimelineApiEnvelope>('/investigation/drop-timeline', {
        params: {
          ...(uid ? { uniqueId: uid } : {}),
          ...(params.wiserDropId != null ? { wiserDropId: params.wiserDropId } : {}),
          ...(prep ? { preparedDropId: prep } : {}),
          scope,
          siemLimit,
        },
      });
      return res.data.data;
    },
    enabled: params.enabled !== false && hasKey,
  });
}

type ModalTimelineBase = Pick<
  UseDropTimelineParams,
  'uniqueId' | 'wiserDropId' | 'preparedDropId' | 'enabled'
>;

export function useDropTimelineStoryAndFull(
  p: ModalTimelineBase & { storyLimit: number; fullLimit: number },
) {
  const storyQ = useDropTimeline({
    ...p,
    scope: 'story',
    siemLimit: p.storyLimit,
  });
  const fullQ = useDropTimeline({
    ...p,
    scope: 'full',
    siemLimit: p.fullLimit,
  });
  return { storyQ, fullQ };
}
